import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";

import {
  checkLocationPermission,
  createPoint,
  filterAgenciesByPointTypes,
  getDistance,
  getDistancesAndSortAgencies,
  getEmptyListbox,
  getFilteredAgenciesV2,
  getParamsFromUrl,
  setCurrentGeolocation,
} from "../utilities/utilities";
import { getPickupPoints } from "../services/getPickupPoints";
import Banner from "./Banner";
import SharePopUp from "./SharePopUp";
import ListBox from "./ListBox";
import MapComponent from "./Map";
import SearchBar from "./Search";
import LazyLoadListBox from "./LazyLoadListBox";
import { getPointTypes } from "../services/getPointTypes";
import Filters from "./Filters";
import { getFiltersController } from "../services/getFiltersController";
import { getAllClientServices } from "../services/getAllClientServices";
import SideFilters from "./SideFilters";
import { getAdditionalServices } from "../services/getAdditionalServices";
import ButtonFilters from "./ButtonFilters";
import ListBoxDetailsCellPhone from "./ListBoxDetailsCellPhone";


const MAX_MARKERS_ON_MAP = 500;

const thinArrayForMap = (array, max) => {
  if (!array || array.length === 0) return [];
  if (array.length <= max) return array;
  return array.slice(0, max)
};

const GeneralContainer = () => {
  const params = getParamsFromUrl();
  const selectedId = params.id;
  const isBannerEnabled = params.isBannerEnabled;
  const pointTypesFromUrl = params.pointTypes;
  const clientServicesFromUrl = params.clientServices;
  const additionalServicesFromUrl = params.additionalServices;
  const filtersControllerFromUrl = params.filtersController;

  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const [map, setMap] = useState(null);

  const [isFiltersMenuOpened, setIsFiltersMenuOpened] = useState(false);

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isPointTypesLoaded, setIsPointTypesLoaded] = useState(false);
  const [isFilterControllerLoaded, setIsFilterControllerLoaded] =
    useState(false);
  const [isAllClientServicesLoaded, setIsAllClientServicesLoaded] =
    useState(false);
  const [isAdditionalServicesLoaded, setIsAdditionalServicesLoaded] =
    useState(false);

  const [agencies, setAgencies] = useState();
  const [agenciesInsideBoundaries, setAgenciesInsideBoundaries] = useState();
  const [topAgency, setTopAgency] = useState();
  const [selectedAgency, setSelectedAgency] = useState();

  const [listboxDetailsCellphoneOpen, setListboxDetailsCellphoneOpen] =
    useState({ state: false, agency: null });

  const [additionalServices, setAdditionalServices] = useState(null);
  const [clientServices, setClientServices] = useState(null);
  const [filtersController, setFiltersController] = useState(null);

  const [pointTypesWithCheck, setPointTypesWithCheck] = useState(null);

  const [allClientServicesWithCheck, setAllClientServicesWithCheck] =
    useState(null);
  const [additionalServicesWithCheck, setAdditionalServicesWithCheck] =
    useState();
  const [filtersControllerWithCheck, setFiltersControllerWithCheck] =
    useState();

  const [currentFilterPointTypeCode, setCurrentFilterPointTypeCode] =
    useState(-1);
  const [currentFilterClientService, setCurrentFilterClientService] =
    useState(-1);

  const [selectedMarkerSize, setSelectedMarkerSize] = useState(60);

  const [selectedSwitchButtonValue, setSelectedSwitchButtonValue] =
    useState("option-location");

  const [idCurrentInfoWindow, setIdCurrentInfoWindow] = useState(-1);

  const [isSharePopUpOpen, setIsSharePopUpOpen] = useState(false);
  const [infoToShare, setInfoToShare] = useState({
    id: "no info",
    title: "no info",
    address: "no info",
  });

  const [center, setCenter] = useState({
    lat: -33.4488897,
    lng: -70.6692655,
  });
  const [currentLocation, setCurrentLocation] = useState(center);
  const [zoom, setZoom] = useState(14);

  const [searchBarText, setSearchBarText] = useState("");

  const listBoxRef = useRef(null);

  useEffect(() => {
    
    Promise.all([getPointTypes(), getPickupPoints()]).then(
      ([pointTypesResponse, agenciesResponse]) => {
        
        setIsPointTypesLoaded(true);
        setIsDataLoaded(true);

        const auxPointTypes = pointTypesResponse.pointTypes?.map(
          (pointType) => ({
            ...pointType, 
            checked: pointTypesFromUrl?.includes(Number(pointType.code)) || false 
          })
        );

        setPointTypesWithCheck(auxPointTypes);
        syncPointTypesQueryParam(auxPointTypes);

        let newCenter = center;
        checkLocationPermission().then((permission) => {
          if (permission === "granted") {
            newCenter = setCurrentGeolocation(
              setCurrentLocation,
              setCenter,
              center
            );
          }
        });

        const sortedAgencies = getDistancesAndSortAgencies(agenciesResponse, newCenter);
        const agenciesFilteredByPointTypes = filterAgenciesByPointTypes(sortedAgencies.agencies, auxPointTypes);
        setAgencies({agencies: agenciesFilteredByPointTypes});

      }
    );

  }, []);

  useEffect(() => {
    if (!isFilterControllerLoaded) {
      getFiltersController().then((filtersControllerResponse) => {
        setIsFilterControllerLoaded(true);
        setFiltersController(filtersControllerResponse);

        const auxFiltersController =
          filtersControllerResponse.filtersControllers?.map((filter) => ({
            ...filter,
            checked:
              filtersControllerFromUrl?.includes(Number(filter.code)) ||
              false,
          }));

        setFiltersControllerWithCheck(auxFiltersController);
        syncFiltersControllerQueryParam(auxFiltersController);
      });
    }
  }, [isFilterControllerLoaded, filtersControllerFromUrl]);

  useEffect(() => {
    if (!isAllClientServicesLoaded) {
      getAllClientServices().then((allClientServicesResponse) => {
        setIsAllClientServicesLoaded(true);
        setClientServices(allClientServicesResponse);

        const auxClientServices = allClientServicesResponse.services
          ?.sort((a, b) => a.description.localeCompare(b.description))
          ?.map((service) => ({ 
            ...service, 
            checked: clientServicesFromUrl?.includes(Number(service.code)) || false, 
          }));
        setAllClientServicesWithCheck(auxClientServices);
        syncClientServicesQueryParam(auxClientServices);
      });
    }
  }, [isAllClientServicesLoaded, clientServicesFromUrl]);

  useEffect(() => {
    if (!isAdditionalServicesLoaded) {
      getAdditionalServices().then((additionalServicesResponse) => {
        setIsAdditionalServicesLoaded(true);
        setAdditionalServices(additionalServicesResponse);

        const auxServices = additionalServicesResponse.services?.map(
          (service) => ({ 
            ...service, 
            checked: additionalServicesFromUrl?.includes(Number(service.code)) || false, 
          })
        );
        setAdditionalServicesWithCheck(auxServices);
        syncAdditionalServicesQueryParam(auxServices);
      });
    }
  }, [isAdditionalServicesLoaded, additionalServicesFromUrl]);

  useEffect(() => {
    if (!agencies || !selectedId) return;

    handleBluexMarkerClick(selectedId);
}, [agencies, selectedId]);

  useEffect(() => {
    if (!agencies) return;
    const sortedAgencies = getDistancesAndSortAgencies(
      agencies.agencies,
      currentLocation
    );
    setAgencies(sortedAgencies);
    setTopAgency(null);

    scrollListboxToTop();
  }, [currentLocation]);

  const filteredAgenciesForMap = useMemo(() => {
    if (!agencies || !agencies.agencies) return [];

    const safePointTypes = pointTypesWithCheck ?? [];
    const safeClientServices = allClientServicesWithCheck ?? [];
    const safeFiltersController = filtersControllerWithCheck ?? [];
    const safeAdditionalServices = additionalServicesWithCheck ?? [];

    try {
      return getFilteredAgenciesV2(
        agencies.agencies,
        agencies.agencies,
        null,
        safePointTypes,
        safeClientServices,
        safeFiltersController,
        safeAdditionalServices,
        selectedSwitchButtonValue,
        searchBarText
      ) || [];
    } catch (e) {
      console.error("Error en getFilteredAgenciesV2 (map):", e);
      return agencies.agencies;
    }
  }, [
    agencies,
    pointTypesWithCheck,
    allClientServicesWithCheck,
    filtersControllerWithCheck,
    additionalServicesWithCheck,
    selectedSwitchButtonValue,
    searchBarText,
  ]);

  const limitedFilteredAgenciesForMap = useMemo(() => {
    if (!filteredAgenciesForMap || filteredAgenciesForMap.length === 0) {
      return [];
    }
    const inBoundsIds = new Set(
      (agenciesInsideBoundaries || []).map((a) => a.agencyId)
    );
     const centerPoint = { lat: center.lat, lng: center.lng };
    const enriched = filteredAgenciesForMap.map((agency) => {
      const agencyLocation = {
        lat: agency.address.geolocation.latitude,
        lng: agency.address.geolocation.longitude,
      };
       const distanceToCenter = getDistance(centerPoint, agencyLocation);
      const isInBounds = inBoundsIds.has(agency.agencyId);
       return { agency, distanceToCenter, isInBounds };
    });
    
    // 1) primero las que están dentro de los bounds
    // 2) dentro de cada grupo, por distancia al centro
    enriched.sort((a, b) => {
      if (a.isInBounds !== b.isInBounds) {
        return a.isInBounds ? -1 : 1;
      }
      return a.distanceToCenter - b.distanceToCenter;
    });
    // Quitamos metadata y aplicamos límite
    const orderedAgencies = enriched.map((e) => e.agency);
    return thinArrayForMap(orderedAgencies, MAX_MARKERS_ON_MAP);
  }, [
    filteredAgenciesForMap,
    agenciesInsideBoundaries,
    center,
  ]);

  const handleOpenPopUp = useCallback((infoToShare) => {
    setIsSharePopUpOpen(true);
    setInfoToShare(infoToShare);
  }, []);

  const handleClosePopUp = useCallback(() => {
    setIsSharePopUpOpen(false);
    setInfoToShare({ id: "no info", title: "no info", address: "no info" });
  }, []);

  const handleClickListboxTitle = useCallback(
    (id) => {
      if (!agencies || !agencies.agencies) return;

      const auxAgency = agencies.agencies.find(
        (agency) => agency.agencyId == id
      );

      if (!auxAgency) return;

      if (screenSize.width < 768) {
        setListboxDetailsCellphoneOpen({ state: true, agency: auxAgency });
      }

      setSelectedMarkerSize((prev) => (prev === 60 ? 59 : 60));

      setSelectedAgency(auxAgency);

      setTopAgency(null);

      setIdCurrentInfoWindow(id);

      const newCenter = {
        lat: auxAgency.address.geolocation.latitude,
        lng: auxAgency.address.geolocation.longitude,
      };
      setCenter(newCenter);
      setZoom((prevZoom) => (prevZoom === 16 ? 15 : 16));
    },
    [agencies, screenSize.width]
  );


  const handleBluexMarkerClick = (id) => {
    setSelectedMarkerSize((prev) => (prev === 60 ? 59 : 60));

    const auxAgency = agencies.agencies.find((agency) => {
      if (agency.agencyId == id && screenSize.width < 768) {
        setListboxDetailsCellphoneOpen({ agency: agency, state: true });
      }
      return agency.agencyId == id;
    });
    setIdCurrentInfoWindow(id);

    scrollListboxToTop();

    if (auxAgency) {
      setTopAgency(auxAgency);
      setSelectedAgency(auxAgency);
    }

    return auxAgency;
  };

  const setClientServicesCheck = (newState, code) => {
    setAllClientServicesWithCheck((prev) => {
      const updated = prev?.map((service) => {
        if (service.code === code) {
          return { ...service, checked: newState };
        }
        return service;
      });

      syncClientServicesQueryParam(updated);
      return updated;
    });
  };

  const setPointTypesCheck = (newState, code) => {

    setPointTypesWithCheck((prev) => {
      const updated = prev?.map((pointType) => {
        if (pointType.code === code) {
          return { ...pointType, checked: newState };
        }
        return pointType;
      });

      syncPointTypesQueryParam(updated);
      return updated;
    });

  };

  const setFilterControllerCheck = (newState, code) => {
    setFiltersControllerWithCheck((prev) => {
      const updated = prev?.map((filter) => {
        if (filter.code === code) {
          return { ...filter, checked: newState };
        }
        return filter;
      });

      syncFiltersControllerQueryParam(updated);
      return updated;
    });
  };
  
  const setAdditionalServiceCheck = (newState, code) => {
    setAdditionalServicesWithCheck((prev) => {
      const updated = prev?.map((service) => {
        if (service.code === code) {
          return { ...service, checked: newState };
        }
        return service;
      });

      syncAdditionalServicesQueryParam(updated);
      return updated;
    });
    
  };

  const resetFilters = () => {
    setPointTypesWithCheck((prev) => {
      const updated = prev?.map((pointType) => ({
        ...pointType,
        checked: false,
      }));
      syncPointTypesQueryParam(updated);
      return updated;
    });

    setAllClientServicesWithCheck((prev) => {
      const updated = prev?.map((service) => ({
        ...service,
        checked: false,
      }));
      syncClientServicesQueryParam(updated);
      return updated;
    });

    setFiltersControllerWithCheck((prev) => {
      const updated = prev?.map((controller) => ({
        ...controller,
        checked: false,
      }));
      syncFiltersControllerQueryParam(updated);
      return updated;
    });
    
    setAdditionalServicesWithCheck((prev) => {
      const updated = prev?.map((service) => ({
        ...service,
        checked: false,
      }));
      syncAdditionalServicesQueryParam(updated);
      return updated;
    });
    setSelectedSwitchButtonValue("option-location");
    setSearchBarText("");
  };

  const syncPointTypesQueryParam = (pointTypesArray) => {
    if (!pointTypesArray) return;

    const checkedCodes = pointTypesArray
      .filter((pt) => pt.checked)
      .map((pt) => pt.code)
      .filter((code) => code !== null && code !== undefined && code !== "");

    const params = new URLSearchParams(window.location.search);

    if (checkedCodes.length > 0) {
      params.set("point-types", checkedCodes.join("-"));
    } else {
      params.delete("point-types");
    }

    const queryString = params.toString();
    const newRelativePathQuery =
      window.location.pathname + (queryString ? `?${queryString}` : "");

    window.history.replaceState(null, "", newRelativePathQuery);
  };

  const scrollListboxToTop = () => {
    if (listBoxRef.current) {
      listBoxRef.current.scrollTop = 0;
    }
  };

  const syncClientServicesQueryParam = (clientServicesArray) => {
    if (!clientServicesArray) return;

    const checkedCodes = clientServicesArray
      .filter((service) => service.checked)
      .map((service) => service.code)
      .filter(
        (code) => code !== null && code !== undefined && code !== ""
      );

    const params = new URLSearchParams(window.location.search);

    if (checkedCodes.length > 0) {
      params.set("client-services", checkedCodes.join("-"));
    } else {
      params.delete("client-services");
    }

    const queryString = params.toString();
    const newRelativePathQuery =
      window.location.pathname + (queryString ? `?${queryString}` : "");

    window.history.replaceState(null, "", newRelativePathQuery);
  };

  const syncAdditionalServicesQueryParam = (additionalServicesArray) => {
    if (!additionalServicesArray) return;

    const checkedCodes = additionalServicesArray
      .filter((service) => service.checked)
      .map((service) => service.code)
      .filter(
        (code) => code !== null && code !== undefined && code !== ""
      );

    const params = new URLSearchParams(window.location.search);

    if (checkedCodes.length > 0) {
      params.set("additional-services", checkedCodes.join("-"));
    } else {
      params.delete("additional-services");
    }

    const queryString = params.toString();
    const newRelativePathQuery =
      window.location.pathname + (queryString ? `?${queryString}` : "");

    window.history.replaceState(null, "", newRelativePathQuery);
  };

  const syncFiltersControllerQueryParam = (filtersControllerArray) => {
    if (!filtersControllerArray) return;

    const checkedCodes = filtersControllerArray
      .filter((filter) => filter.checked)
      .map((filter) => filter.code)
      .filter(
        (code) => code !== null && code !== undefined && code !== ""
      );

    const params = new URLSearchParams(window.location.search);

    if (checkedCodes.length > 0) {
      params.set("filters-controller", checkedCodes.join("-"));
    } else {
      params.delete("filters-controller");
    }

    const queryString = params.toString();
    const newRelativePathQuery =
      window.location.pathname + (queryString ? `?${queryString}` : "");

    window.history.replaceState(null, "", newRelativePathQuery);
  };

  const callbackSetIdCurrentInfoWindow = (id) => {
    setIdCurrentInfoWindow(id);
  };
  const updateCenterAndCurrentLocation = useCallback((latLng) => {
    setCenter(latLng);
    setCurrentLocation(latLng);
  }, []);

  const filteredTopAgency = useMemo(() => {
    if (!topAgency) return [];
    try {
      return (
        getFilteredAgenciesV2(
          [topAgency],
          [topAgency],
          null,
          pointTypesWithCheck ?? [],
          allClientServicesWithCheck ?? [],
          filtersControllerWithCheck ?? [],
          additionalServicesWithCheck ?? [],
          selectedSwitchButtonValue,
          searchBarText
        ) || []
      );
    } catch (e) {
      console.error("Error en getFilteredAgenciesV2 (topAgency):", e);
      return [];
    }
  }, [
    topAgency,
    pointTypesWithCheck,
    allClientServicesWithCheck,
    filtersControllerWithCheck,
    additionalServicesWithCheck,
    selectedSwitchButtonValue,
    searchBarText,
  ]);

  const filteredAgenciesInsideBoundaries = useMemo(() => {
    if (!agenciesInsideBoundaries || !agencies || !agencies.agencies) {
      return [];
    }

    try {
      return (
        getFilteredAgenciesV2(
          agenciesInsideBoundaries,
          agencies.agencies,
          topAgency,
          pointTypesWithCheck ?? [],
          allClientServicesWithCheck ?? [],
          filtersControllerWithCheck ?? [],
          additionalServicesWithCheck ?? [],
          selectedSwitchButtonValue,
          searchBarText
        ) || []
      );
    } catch (e) {
      console.error("Error en getFilteredAgenciesV2 (listbox):", e);
      return [];
    }
  }, [
    agenciesInsideBoundaries,
    agencies,
    topAgency,
    pointTypesWithCheck,
    allClientServicesWithCheck,
    filtersControllerWithCheck,
    additionalServicesWithCheck,
    selectedSwitchButtonValue,
    searchBarText,
  ]);

  const limitedFilteredAgenciesInsideBoundaries = useMemo(() => {
    return thinArrayForMap(filteredAgenciesInsideBoundaries, MAX_MARKERS_ON_MAP);
  }, [filteredAgenciesInsideBoundaries]);

  const isTopAgencyVisible = filteredTopAgency.length > 0;

  const shouldShowEmptyListbox =
    agenciesInsideBoundaries &&
    filteredAgenciesInsideBoundaries.length === 0 &&
    (!topAgency || !isTopAgencyVisible);

  return (
    <div className="overflow-y-hidden vh-100 d-flex flex-column ">
      {isSharePopUpOpen && (
        <div className="position-absolute z-index-99999 ">
          <SharePopUp onClose={handleClosePopUp} infoToShare={infoToShare} />
        </div>
      )}
      {filtersController && (
        <SideFilters
          pointTypesArray={pointTypesWithCheck}
          setPointTypesCheck={setPointTypesCheck}
          clientServicesArray={allClientServicesWithCheck}
          setClientServicesCheck={setClientServicesCheck}
          filtersControllerArray={filtersControllerWithCheck}
          setFilterControllerCheck={setFilterControllerCheck}
          otherFiltersArray={additionalServicesWithCheck}
          setOtherFilterCheck={setAdditionalServiceCheck}
          setMenuOpen={setIsFiltersMenuOpened}
          selectedSwitchButtonValue={selectedSwitchButtonValue}
          setSelectedSwitchButtonValue={setSelectedSwitchButtonValue}
          resetFilters={resetFilters}
          isOpen={isFiltersMenuOpened}
        />
      )}
      {
        <ListBoxDetailsCellPhone
          onClickBack={() =>
            setListboxDetailsCellphoneOpen({
              ...listboxDetailsCellphoneOpen,
              state: false,
            })
          }
          point={createPoint(listboxDetailsCellphoneOpen.agency)}
          currentLocation={currentLocation}
          onClickShare={handleOpenPopUp}
          isOpen={listboxDetailsCellphoneOpen.state}
          allAdditionalServices={additionalServices?.services}
          clientServices={clientServices?.services}
        />
      }
      <div className="banner-searchbar-container position-relative">
        {isBannerEnabled && <Banner />}
        <div className={isBannerEnabled && "mt-15px hide-on-cellphone"}>
          <div
            className={
              (!isBannerEnabled && "pt-2") +
              " px-4 pb-2 searchbar-container hide-on-cellphone"
            }
          >
            <div>
              <SearchBar
                className={`position-relative z-index-9999`}
                updateMapPosition={updateCenterAndCurrentLocation}
                selectedButtonValue={selectedSwitchButtonValue}
                setSelectedButtonValue={setSelectedSwitchButtonValue}
                text={searchBarText}
                setText={setSearchBarText}
                screenSize={screenSize}
              />
            </div>

            <div className={"pb-1 pt-3 " + "hide-on-cellphone"}>
              <Filters
                isPointTypesLoaded={isPointTypesLoaded}
                isAllServicesLoaded={isAllClientServicesLoaded}
                sortedClientServices={allClientServicesWithCheck}
                setClientServicesCheck={setClientServicesCheck}
                pointTypesWithCheck={pointTypesWithCheck}
                setPointTypesCheck={setPointTypesCheck}
                additionalServicesWithCheck={additionalServicesWithCheck}
                setAdditionalServicesCheck={setAdditionalServiceCheck}
                filtersControllerWithCheck={filtersControllerWithCheck}
                setFilterControllerCheck={setFilterControllerCheck}
                isFilterControllerLoaded={isFilterControllerLoaded}
                resetFilters={resetFilters}
                map={map}
                setCenter={setCenter}
                setIsFiltersMenuOpened={setIsFiltersMenuOpened}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className={`position-relative ${
          isBannerEnabled
            ? `map-and-listbox-container-w-banner`
            : `map-and-listbox-container-w-nobanner`
        }`}
      >
        <MapComponent
          isMarkerShown
          isDataLoaded={isDataLoaded}
          agencies={agencies}
          selectedAgencyId={selectedAgency?.agencyId}
          center={center}
          setCenter={setCenter}
          currentLocation={currentLocation}
          idCurrentInfoWindow={idCurrentInfoWindow}
          zoom={zoom}
          handleBluexMarkerClick={handleBluexMarkerClick}
          setIdCurrentInfoWindow={callbackSetIdCurrentInfoWindow}
          selectedMarkerSize={selectedMarkerSize}
          currentFilterPointTypeCode={currentFilterPointTypeCode}
          currentFilterClientServiceCode={currentFilterClientService}
          isFilterControllerLoaded={isFilterControllerLoaded}
          filtersController={filtersController}
          setAgenciesInsideBoundaries={setAgenciesInsideBoundaries}
          map={map}
          setMap={setMap}
          setCurrentGeolocation={() =>
            setCurrentGeolocation(setCurrentLocation, setCenter, center)
          }
          filteredAgencies={limitedFilteredAgenciesForMap}
        />
        <div className="p-3 d-flex align-items-center gap-3 hide-on-desktop-and-tablet">
          <div className="flex-grow-0">
            <ButtonFilters
              setIsFilterMenuOpened={setIsFiltersMenuOpened}
              iconSize="32px"
            />
          </div>
          <div className="flex-grow-1">
            <SearchBar
              className={`position-relative z-index-9999`}
              updateMapPosition={updateCenterAndCurrentLocation}
              selectedButtonValue={selectedSwitchButtonValue}
              setSelectedButtonValue={setSelectedSwitchButtonValue}
              text={searchBarText}
              setText={setSearchBarText}
              screenSize={screenSize}
            />
          </div>
        </div>
        <div
          ref={listBoxRef}
          className={
            (isBannerEnabled
              ? "list-box-container-scroll-w-banner"
              : "list-box-container-scroll-w-nobanner") + " list-box-container "
          }
        >
                    {isDataLoaded ? (
            <>
              {/* Top agency */}
              {isTopAgencyVisible &&
                filteredTopAgency.map((a) => (
                  <div
                    key={a.agencyId}
                    className={
                      selectedAgency?.agencyId === a.agencyId
                        ? "selected-list-box"
                        : ""
                    }
                  >
                    <ListBox
                      point={createPoint(a)}
                      id={a.agencyId}
                      onClickTitle={handleClickListboxTitle}
                      onClickShare={handleOpenPopUp}
                      currentLocation={
                        currentLocation.lat + "," + currentLocation.lng
                      }
                      allAdditionalServices={additionalServices?.services}
                      clientServices={clientServices?.services}
                    />
                  </div>
                ))}

              {/* Sin resultados dentro de bounds */}
              {shouldShowEmptyListbox && getEmptyListbox()}

              {/* Resto de agencias dentro de bounds */}
              {!shouldShowEmptyListbox &&
                limitedFilteredAgenciesInsideBoundaries.map((agency) => {
                  const point = createPoint(agency);
                  return (
                    <div
                      key={agency.agencyId}
                      className={
                        selectedAgency?.agencyId === agency.agencyId
                          ? "selected-list-box"
                          : ""
                      }
                    >
                      <ListBox
                        point={point}
                        id={agency.agencyId}
                        onClickTitle={handleClickListboxTitle}
                        onClickShare={handleOpenPopUp}
                        currentLocation={
                          currentLocation.lat + "," + currentLocation.lng
                        }
                        allAdditionalServices={additionalServices?.services}
                        clientServices={clientServices?.services}
                      />
                    </div>
                  );
                })}
            </>
          ) : (
            <>
              <LazyLoadListBox />
              <LazyLoadListBox />
              <LazyLoadListBox />
              <LazyLoadListBox />
              <LazyLoadListBox />
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default GeneralContainer;
